import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * "Visual diary" band — a coverflow carousel of admin-managed photos and videos.
 *
 * Tiles carry a category, which becomes a filter pill above the carousel; a tile
 * flagged as a video shows a play badge and opens its clip in the lightbox.
 * Everything is driven by the section rows the admin panel writes, so the markup
 * degrades to nothing when the section has no tiles.
 */
function DiaryGallery({ section }) {
  const tiles = useMemo(
    () => (section.images || []).filter((tile) => tile.src),
    [section.images]
  );

  const categories = useMemo(() => {
    const seen = [];
    tiles.forEach((tile) => {
      const name = (tile.category || '').trim();
      if (name && !seen.includes(name)) seen.push(name);
    });
    return seen;
  }, [tiles]);

  const [active, setActive] = useState('');   // '' = every category
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const trackRef = useRef(null);

  const visible = useMemo(
    () => (active ? tiles.filter((tile) => (tile.category || '').trim() === active) : tiles),
    [tiles, active]
  );

  // Filtering changes the deck, so re-centre on the first slide.
  useEffect(() => { setIndex(0); }, [active]);

  const count = visible.length;
  const step = useCallback((delta) => {
    setIndex((current) => (count ? (current + delta + count) % count : 0));
  }, [count]);

  // Arrow keys move the deck once it has focus; Escape closes the lightbox.
  const onKeyDown = (event) => {
    if (event.key === 'ArrowLeft') { step(-1); event.preventDefault(); }
    if (event.key === 'ArrowRight') { step(1); event.preventDefault(); }
  };

  useEffect(() => {
    if (!lightbox) return undefined;
    const close = (event) => { if (event.key === 'Escape') setLightbox(null); };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [lightbox]);

  // Touch swipe on the deck.
  const touch = useRef(null);
  const onTouchStart = (event) => { touch.current = event.touches[0].clientX; };
  const onTouchEnd = (event) => {
    if (touch.current === null) return;
    const delta = event.changedTouches[0].clientX - touch.current;
    if (Math.abs(delta) > 40) step(delta < 0 ? 1 : -1);
    touch.current = null;
  };

  if (!tiles.length) return null;

  const theme = section.theme === 'dark' ? 'is-dark' : 'is-light';

  return (
    <section className={`experience-component admin-managed-section diary-section ${theme}`}
      aria-label={section.alt || section.title || 'Gallery'}>

      <header className="diary-head">
        {section.eyebrow && <p className="diary-eyebrow">{section.eyebrow}</p>}
        {section.title && <h2 className="diary-title">{section.title}</h2>}
        {section.subtitle && <p className="diary-subtitle">{section.subtitle}</p>}
      </header>

      {(categories.length > 0 || section.ctaLabel) && (
        <div className="diary-filters" role="tablist" aria-label="Gallery categories">
          {categories.length > 0 && (
            <button type="button" role="tab" aria-selected={active === ''}
              className={'diary-pill' + (active === '' ? ' is-active' : '')}
              onClick={() => setActive('')}>
              All
            </button>
          )}
          {categories.map((name) => (
            <button key={name} type="button" role="tab" aria-selected={active === name}
              className={'diary-pill' + (active === name ? ' is-active' : '')}
              onClick={() => setActive(name)}>
              {name}
            </button>
          ))}
          {section.ctaLabel && (
            <a className="diary-pill diary-pill-cta" href={section.ctaHref || '#'}>
              {section.ctaLabel} <span aria-hidden="true">→</span>
            </a>
          )}
        </div>
      )}

      {count === 0 ? (
        <p className="diary-empty">Nothing in this category yet.</p>
      ) : (
        <div className="diary-stage" ref={trackRef} tabIndex={0} onKeyDown={onKeyDown}
          onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          {visible.map((tile, position) => {
            // Signed distance from the centre, wrapped so the deck loops both ways.
            let offset = position - index;
            if (offset > count / 2) offset -= count;
            if (offset < -count / 2) offset += count;

            const depth = Math.abs(offset);
            if (depth > 3) return null;

            const style = {
              '--offset': offset,
              '--depth': depth,
              zIndex: 10 - depth,
            };

            return (
              <figure key={tile.id || tile.src + position}
                className={'diary-card' + (offset === 0 ? ' is-active' : '')}
                style={style}
                aria-hidden={offset === 0 ? undefined : 'true'}>
                <button type="button" className="diary-card-hit"
                  onClick={() => (offset === 0 ? setLightbox(tile) : setIndex(position))}>
                  <img src={tile.src} alt={tile.alt || tile.caption || ''}
                    loading="lazy" decoding="async" />
                  {tile.kind === 'video' && <span className="diary-play" aria-hidden="true" />}
                  <span className="diary-expand" aria-hidden="true" />
                  <span className="sr-only">
                    {offset === 0 ? `Open ${tile.alt || 'item'}` : `Show ${tile.alt || 'item'}`}
                  </span>
                </button>
                {tile.caption && offset === 0 && (
                  <figcaption className="diary-caption">{tile.caption}</figcaption>
                )}
              </figure>
            );
          })}
        </div>
      )}

      {count > 1 && (
        <div className="diary-nav">
          <button type="button" className="diary-arrow" onClick={() => step(-1)} aria-label="Previous">←</button>
          <button type="button" className="diary-arrow" onClick={() => step(1)} aria-label="Next">→</button>
        </div>
      )}

      {lightbox && (
        <div className="diary-lightbox" role="dialog" aria-modal="true"
          onClick={() => setLightbox(null)}>
          <button type="button" className="diary-lightbox-close" aria-label="Close">×</button>
          <div className="diary-lightbox-inner" onClick={(event) => event.stopPropagation()}>
            {lightbox.kind === 'video' && lightbox.videoSrc ? (
              <video src={lightbox.videoSrc} poster={lightbox.src} controls autoPlay playsInline />
            ) : (
              <img src={lightbox.src} alt={lightbox.alt || ''} />
            )}
            {(lightbox.caption || lightbox.href) && (
              <div className="diary-lightbox-meta">
                {lightbox.caption && <span>{lightbox.caption}</span>}
                {lightbox.href && <a href={lightbox.href}>Discover →</a>}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export default DiaryGallery;
